/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import queryClient from 'app/queryClient'
import { WidgetId } from 'features/types'
import { LightboxProps } from 'components/visualization/Lightbox/types'
import { getDashboardByReportId as liveReportGetDashboardByReportId } from 'features/liveReport/asyncThunks'
import {
  getThemeFromLocalStorage,
  getLabelsIsReadFromLocalStorage,
  getSidePanelIsReadFromLocalStorage
} from 'utils/localStorageUtil'

import { ToastId, UiSlice, Page } from './types'
import { getDashboard } from '../dashboard/asyncThunks'
import {
  getAutoRefreshInterval,
  shouldAutoRefreshEnable,
  getDefaultStatefromSessionStorage
} from './utils'

const initialState: UiSlice = {
  displayTheme: getThemeFromLocalStorage(),
  toasts: [],
  dashboardViewLayout: {
    presentationMode: false,
    pinMode: false,
    exportMode: false,
    isFilterDropdownOpen: false
  },
  refresh: {
    timestamp: Date.now(),
    interval: 0,
    enabled: false,
    onDemands: {}
  },
  lightbox: {
    slides: [],
    defaultIndex: 0,
    isOpen: false
  },
  page: undefined,
  introductionToolTips: {
    labelsIsRead: getLabelsIsReadFromLocalStorage(),
    sidePanelIsRead: getSidePanelIsReadFromLocalStorage()
  },
  ...getDefaultStatefromSessionStorage()
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setUiState: (state, action) => ({ ...state, ...action.payload }),
    deleteUiState: (state: UiSlice, action: PayloadAction<'dashboard/edit'>) => {
      delete state[action.payload]
    },
    setDisplayTheme: (state, action) => {
      state.displayTheme = action.payload
    },
    addToastID: (
      state,
      action: PayloadAction<{
        toastId: ToastId
      }>
    ) => {
      const { toastId } = action.payload
      state.toasts.push(toastId)
    },
    removeToastID: (
      state,
      action: PayloadAction<{
        toastId: ToastId
      }>
    ) => {
      const { toastId } = action.payload
      const filteredToast = state.toasts.filter((t) => t !== toastId)
      state.toasts = filteredToast
    },
    updateRefreshTimeStamp: (state) => {
      queryClient.invalidateQueries({
        queryKey: [
          state.page === Page.LiveReport ? '/proxy/runSearches' : '/runSearches',
          state.refresh.timestamp
        ],
        exact: false,
        refetchType: 'none'
      })
      state.refresh.timestamp = Date.now()
      state.refresh.onDemands = {}
    },
    updateRefreshOnDemands: (
      state,
      action: PayloadAction<{
        widgetId: WidgetId
      }>
    ) => {
      const { widgetId } = action.payload
      queryClient.invalidateQueries({
        queryKey: [
          state.page === Page.LiveReport ? '/proxy/runSearches' : '/runSearches',
          state.refresh.timestamp,
          widgetId
        ],
        exact: false,
        refetchType: 'none'
      })
      state.refresh.onDemands[widgetId] = Date.now()
    },
    openLightbox: (
      state,
      action: PayloadAction<{
        slides: LightboxProps['slides']
        defaultIndex: LightboxProps['defaultIndex']
      }>
    ) => {
      const { defaultIndex, slides } = action.payload
      state.lightbox.slides = slides
      state.lightbox.defaultIndex = defaultIndex
      state.lightbox.isOpen = true
    },
    closeLightbox: (state) => {
      state.lightbox.isOpen = false
    },
    setPage: (state, action) => {
      state.page = action.payload
    },
    setPresentationMode: (state, action: PayloadAction<boolean>) => {
      state.dashboardViewLayout.presentationMode = action.payload
      state.refresh.enabled = state.refresh.interval !== 0 && action.payload
    },
    setPinMode: (state, action: PayloadAction<boolean>) => {
      state.dashboardViewLayout.pinMode = action.payload
    },
    setIsFilterDropdownOpen: (state, action: PayloadAction<boolean>) => {
      state.dashboardViewLayout.isFilterDropdownOpen = action.payload
    },
    setExportMode: (state, action: PayloadAction<boolean>) => {
      state.dashboardViewLayout.exportMode = action.payload
    },
    setIntroductionToolTips: (
      state,
      action: PayloadAction<Partial<UiSlice['introductionToolTips']>>
    ) => {
      state.introductionToolTips = {
        ...state.introductionToolTips,
        ...action.payload
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) =>
          action.type === getDashboard.pending.type ||
          action.type === liveReportGetDashboardByReportId.pending.type,
        (state) => {
          state.refresh.enabled = false
          state.refresh.onDemands = {}
        }
      )
      .addMatcher(
        (action) =>
          action.type === getDashboard.fulfilled.type ||
          action.type === liveReportGetDashboardByReportId.fulfilled.type,
        (state, action) => {
          const { autoRefreshInterval } = action.payload
          state.refresh.enabled = shouldAutoRefreshEnable({
            interval: autoRefreshInterval!,
            isPresentationMode:
              action.type === getDashboard.fulfilled.type
                ? state.dashboardViewLayout.presentationMode
                : true
          })
          state.refresh.interval = getAutoRefreshInterval(autoRefreshInterval!)
          state.refresh.onDemands = {}
        }
      )
      .addMatcher(
        (action) =>
          action.type === getDashboard.rejected.type ||
          action.type === liveReportGetDashboardByReportId.rejected.type,
        (state) => {
          state.refresh.enabled = false
          state.refresh.onDemands = {}
        }
      )
  }
})

export const {
  setUiState,
  deleteUiState,
  setDisplayTheme,
  addToastID,
  removeToastID,
  updateRefreshTimeStamp,
  updateRefreshOnDemands,
  openLightbox,
  closeLightbox,
  setPage,
  setPresentationMode,
  setPinMode,
  setIsFilterDropdownOpen,
  setExportMode,
  setIntroductionToolTips
} = uiSlice.actions

export default uiSlice.reducer
